import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const LanguageButton = styled.button`
  background: transparent;
  color: #000000;
  border: none;
  padding: 8px 15px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #333333;
  }

  &:focus {
    outline: none;
  }
`;

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Create overlay element if it doesn't exist
    if (!document.getElementById('language-transition-overlay')) {
      const overlay = document.createElement('div');
      overlay.id = 'language-transition-overlay';
      document.body.appendChild(overlay);
    }
    
    return () => {
      // Clean up on unmount if needed
    };
  }, []);
  
  // Helper function to update SEO meta tags based on language
  const updateSEOMetaTags = (lang) => {
    // Update HTML lang attribute for SEO
    document.documentElement.lang = lang;
    
    // Update meta description based on language
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      if (lang === 'en') {
        metaDescription.setAttribute('content', 'National Medical Elite is a premium dental clinic in Dammam offering cosmetic dentistry, implants, orthodontics, and more with cutting-edge technology and expert care.');
      } else {
        metaDescription.setAttribute('content', 'النخبة الوطنية الطبية هي عيادة أسنان متميزة في الدمام تقدم طب الأسنان التجميلي وزراعة الأسنان وتقويم الأسنان والمزيد مع تقنيات متطورة ورعاية خبيرة.');
      }
    }
    
    // Update page title dynamically based on language
    if (lang === 'en') {
      document.title = 'National Medical Elite | Premium Dental Care in Dammam';
    } else {
      document.title = 'النخبة الوطنية الطبية | رعاية أسنان متميزة في الدمام';
    }
  };
  
  const toggleLanguage = () => {
    // Prevent multiple clicks during animation
    if (document.getElementById('language-transition-overlay').classList.contains('visible')) return;
    
    // Show overlay
    const overlay = document.getElementById('language-transition-overlay');
    overlay.classList.add('visible');
    
    // Wait for overlay animation to complete before changing language
    setTimeout(() => {
      // Toggle between 'en' and 'ar'
      const newLang = i18n.language === 'en' ? 'ar' : 'en';
      i18n.changeLanguage(newLang);
      
      // Set HTML dir attribute for RTL/LTR
      document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
      
      // Update SEO meta tags for the current language
      updateSEOMetaTags(newLang);
      
      // Store language preference
      localStorage.setItem('language', newLang);
      
      // Hide overlay after language has changed
      setTimeout(() => {
        overlay.classList.remove('visible');
      }, 200);
      
    }, 600);
  };

  // When current language is English, show "Arabic" as an option and vice versa
  const buttonLabel = i18n.language === 'en' ? 'العربية' : 'English';

  return (
    <LanguageButton onClick={toggleLanguage}>
      {buttonLabel}
    </LanguageButton>
  );
};

export default LanguageSwitcher;

// Add global style to index.css or inject a style tag on component mount
if (!document.getElementById('language-transition-styles')) {
  const styleTag = document.createElement('style');
  styleTag.id = 'language-transition-styles';
  styleTag.innerHTML = `
    #language-transition-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: #3858F9;
      z-index: 9999;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.6s cubic-bezier(0.65, 0, 0.35, 1), visibility 0.6s cubic-bezier(0.65, 0, 0.35, 1);
      pointer-events: none;
    }
    
    #language-transition-overlay.visible {
      opacity: 0.97;
      visibility: visible;
    }
    
    /* Scale animation for content when changing language */
    body {
      transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    
    #language-transition-overlay.visible ~ * {
      transform: scale(0.96);
      transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
  `;
  document.head.appendChild(styleTag);
}
